import clsx from "clsx";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { convertPlayerPrice } from "../../../../functions/convertPlayerPrice";
import { extractColors } from "../../../../functions/extractCardColors";
import { getPriceType } from "../../../../functions/getPriceType";
import "../../../../icons";
import {
  owned_icon_true as ownedIcon,
  untradeable_icon_true as untradeableIcon,
} from "../../../../icons";
import { getVersionAssetsSelector } from "../../../../redux/loadedContent/loadedContentSlice";
import { chemStylesToIcon } from "../../../../redux/squadBuilder/squadBuilderUtils";
import Chemistry from "../../Chemistry/Chemistry";
import { PlayStylePlus } from "../PlayStylePlus/PlayStylePlus";

type Props = {
  rating: number;
  chemistry?: {
    currentChemistry: number;
    previousChemistry: number;
    isOutOfPosition: boolean;
  };
  cardName: string;
  preferredPosition: string;
  possiblePositions: string[];
  clubId: number;
  countryId: number;
  leagueId: number;
  versionId: number;
  attributes: number[];
  price: number;
  playerUrl: string;
  chemStyleId: number;
  sbcPrice: number;
  isSbcPlayer: boolean;
  isObjectivePlayer: boolean;
  owned: boolean;
  untradeable: boolean;
  metalId: number;
  isMobile: boolean;
  hasDynamicImage: boolean;
  playStylesPlus: number[];
  hidePrice: boolean;
};

const FC24Player = ({
  rating,
  chemistry,
  cardName,
  clubId,
  countryId,
  leagueId,
  attributes,
  price,
  playerUrl,
  preferredPosition,
  chemStyleId,
  sbcPrice,
  isSbcPlayer,
  isObjectivePlayer,
  owned,
  untradeable,
  isMobile,
  versionId,
  metalId,
  hasDynamicImage,
  playStylesPlus,
  hidePrice,
}: Props) => {
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
  const isAboveLargeDesktop = !useMediaQuery("(max-width: 1441px)");
  const versionAssets = useSelector(getVersionAssetsSelector);
  const { topText, bottomText, cardUrl, isLight, strip } = extractColors({
    versionAssets,
    versionId,
    metalId,
  });

  const playerPriceFrom = getPriceType(
    price,
    sbcPrice,
    isSbcPlayer,
    isObjectivePlayer,
  );

  const playerImage = (
    <img
      alt="player-url"
      className={clsx(
        "absolute flex items-center",
        hasDynamicImage
          ? versionId === 72
            ? "top-[8%] mobile:top-[-4px] right-2 w-16 touch-device:right-0 mobile:w-24 touch-device:w-36"
            : "w-full -top-1 mobile:right-0 mobile:w-28 touch-device:w-36"
          : "w-3/5 mobile:w-[72px] touch-device:w-24 mobile:top-[17%] touch-device:top-[15%] top-[20%] right-[10px] touch-device:right-4",
      )}
      src={playerUrl}
    />
  );

  const topLeftArea = (
    <div
      className={`absolute left-[14%] top-[22%] touch-device:left-[14%] flex flex-col text-left`}
      style={{
        color: topText,
      }}
    >
      <div
        className="text-sm touch-device:text-xl touch-device:-mt-0.5 leading-4 touch-device:leading-6 mobile:-ml-0.5"
        style={{
          fontFamily: "CruyffMedium",
        }}
      >
        {rating}
      </div>

      <div
        className={clsx(
          "text-xtiny touch-device:text-tiny -mt-0.5 touch-device:mt-[-6px]",
          preferredPosition === "CDM" ||
            preferredPosition === "LWB" ||
            preferredPosition === "RWB"
            ? "-ml-0.5"
            : "",
        )}
        style={{
          fontFamily: "CruyffCondensedMedium",
        }}
      >
        {preferredPosition}
      </div>

      <div
        className={clsx("w-fit flex flex-col items-center ml-[-1px] -mt-1.5")}
      >
        {!isMobile && (
          <div
            className="text-tiny touch-device:text-xs"
            style={{
              fontFamily: "UltimateTeamIcons",
            }}
          >
            {chemStylesToIcon[chemStyleId]}
          </div>
        )}
      </div>
    </div>
  );

  const priceSection = (
    <div className="w-full flex absolute mobile:top-1 top-[-6px] touch-device:top-0">
      <div
        className={clsx(
          "flex flex-row gap-x-[2px] px-1 bg-black rounded text-white text-tiny mobile:text-xs touch-device:text-sm border border-[1px] border-[#493422] items-center w-fit mx-auto",
          (playerPriceFrom !== "market" || price === 0) && "py-[1px]",
        )}
      >
        <img
          className={clsx(
            "my-auto",
            playerPriceFrom === "market" || playerPriceFrom === "xp"
              ? "w-[9px] h-[9px] touch-device:w-[13px] touch-device:h-[13px]"
              : "w-[8px] h-[8px] touch-device:w-[10px] touch-device:h-[10px]",
          )}
          src={
            import.meta.env.VITE_APP_ASSETS_BASE_URL +
            "/fc25/misc/" +
            (playerPriceFrom === "sbc"
              ? "sbc.png"
              : playerPriceFrom === "xp"
                ? "xp_not_cached.png"
                : "coin.png")
          }
          alt="ut-coin"
        />

        {!isObjectivePlayer && (price > 0 || sbcPrice > 0) && (
          <span className="leading-[9px] touch-device:leading-3 py-[2px]">
            {convertPlayerPrice(price || sbcPrice)}
          </span>
        )}
        {owned && (
          <div className="w-2 h-2 touch-device:w-3 touch-device:h-3 shrink-0">
            {ownedIcon}
          </div>
        )}
        {untradeable && (
          <div className="w-2 h-2 touch-device:w-3 touch-device:h-3 shrink-0">
            {untradeableIcon}
          </div>
        )}
      </div>
    </div>
  );

  const playStylePlusSection = (
    <div
      className={clsx(
        "absolute left-[3px] mobile:left-1 flex flex-col touch-device:left-[6px]",
        playStylesPlus?.length > 2 ? "top-[42%]" : "top-[50%]",
      )}
    >
      {playStylesPlus?.map((playStylePlusId) => (
        <div
          className="w-2.5 h-2.5 mobile:w-3 mobile:h-3 touch-device:w-4 touch-device:h-4"
          key={playStylePlusId}
        >
          <PlayStylePlus
            primaryColor={bottomText}
            secondaryColor={strip}
            id={playStylePlusId}
          />
        </div>
      ))}
    </div>
  );

  const attributes_ = (position: string) => (
    <div
      className="flex flex-row gap-x-[4px] touch-device:gap-x-[6px] text-xxtiny touch-device:text-xtiny leading-[9px] touch-device:leading-[10px] mt-1 text-center"
      style={{
        fontFamily: "CruyffCondensedMedium",
      }}
    >
      <div className="flex flex-col">
        <div>{position === "GK" ? "DIV" : "PAC"}</div>
        <div
          className="touch-device:text-tiny text-xtiny"
          style={{
            fontFamily: "CruyffMedium",
          }}
        >
          {attributes?.[0]}
        </div>
      </div>
      <div className="flex flex-col">
        <div>{position === "GK" ? "HAN" : "SHO"}</div>
        <div
          className="touch-device:text-tiny text-xtiny"
          style={{
            fontFamily: "CruyffMedium",
          }}
        >
          {attributes?.[1]}
        </div>
      </div>
      <div className="flex flex-col">
        <div>{position === "GK" ? "KIC" : "PAS"}</div>
        <div
          className="touch-device:text-tiny text-xtiny"
          style={{
            fontFamily: "CruyffMedium",
          }}
        >
          {attributes?.[2]}
        </div>
      </div>
      <div className="flex flex-col">
        <div>{position === "GK" ? "REF" : "DRI"}</div>
        <div
          className="touch-device:text-tiny text-xtiny"
          style={{
            fontFamily: "CruyffMedium",
          }}
        >
          {attributes?.[3]}
        </div>
      </div>
      <div className="flex flex-col">
        <div>{position === "GK" ? "SPE" : "DEF"}</div>
        <div
          className="touch-device:text-tiny text-xtiny"
          style={{
            fontFamily: "CruyffMedium",
          }}
        >
          {attributes?.[4]}
        </div>
      </div>
      <div className="flex flex-col">
        <div>{position === "GK" ? "POS" : "PHY"}</div>
        <div
          className="touch-device:text-tiny text-xtiny"
          style={{
            fontFamily: "CruyffMedium",
          }}
        >
          {attributes?.[5]}
        </div>
      </div>
    </div>
  );

  const clubLeagueCountry = (
    <div className="w-full mx-auto justify-center gap-x-1 relative mt-[4px] mobile:mt-[2px]">
      <img
        alt={"country"}
        className="absolute touch-device:w-4 w-3 h-[6px] left-[27%] bottom-[-6px] touch-device:bottom-[-10px] touch-device:h-2  touch-device:left-[25%] mt-[2px]"
        src={`${assetsBaseUrl}/fc25/countries/${countryId}.png`}
      />
      <img
        alt={"league"}
        className="absolute w-2 h-2 -translate-x-1/2 left-1/2 touch-device:w-3 touch-device:h-3"
        src={`${assetsBaseUrl}/fc25/leagues/${
          isLight ? "" : "dark/"
        }${leagueId}.png`}
      />
      <img
        alt={"club"}
        className="absolute touch-device:w-3 touch-device:h-3 h-2 w-2 right-[32%] touch-device:right-[30%]"
        src={`${assetsBaseUrl}/fc25/clubs/${
          isLight ? "" : "dark/"
        }${clubId}.png`}
      />
    </div>
  );

  const bottomArea = (
    <div
      className="absolute flex flex-col top-[66%] translate-x-1/2 right-1/2 w-full mobile:w-auto"
      style={{
        color: bottomText,
      }}
    >
      <div
        className="text-xtiny w-full mobile:w-auto whitespace-nowrap leading-[8px] mobile:leading-[1px] touch-device:text-xs text-center touch-device:leading-[4px] mt-[2px]"
        style={{
          fontFamily: "CruyffMedium",
        }}
      >
        {cardName}
      </div>
      {!isMobile && attributes_(preferredPosition)}
      {clubLeagueCountry}
    </div>
  );

  const chemistrySection = (
    <div className="absolute w-fit -left-0.5 bottom-3 mobile:left-[2px] touch-device:left-2">
      <Chemistry
        chemistry={chemistry?.currentChemistry as 0 | 1 | 2 | 3}
        previousChemistry={chemistry?.previousChemistry as 0 | 1 | 2 | 3}
        isOutOfPosition={chemistry?.isOutOfPosition}
        weight={isAboveLargeDesktop ? 1.1 : !isMobile ? 0.8 : 0.7}
        backgroundColor="bg-gray-900"
      />
    </div>
  );

  return (
    <div className="relative w-20 mobile:w-28 touch-device:w-36 text-[#412b18] ">
      <img alt="type" src={cardUrl} />
      {!hidePrice && priceSection}
      {playerImage}
      {topLeftArea}
      {bottomArea}
      {chemistry && chemistrySection}
      {playStylesPlus?.length > 0 && playStylePlusSection}
    </div>
  );
};

export default FC24Player;
